import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Sequence, SequenceStep } from '../../types/campaign';
import { SequenceStatusBadge } from '../../components/Sequences/SequenceStatusBadge';
import { SequenceLoadingSkeleton } from '../../components/Sequences/SequenceLoadingSkeleton';
import { SequenceTimeline } from '../../components/Sequences/SequenceTimeline';
import { SequenceStepCard } from '../../components/Sequences/SequenceStepCard';

export const SequenceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  // Fetch sequence
  const { data: sequence, isLoading, error } = useQuery<Sequence>({
    queryKey: ['sequence', id],
    queryFn: async () => {
      const response = await api.sequences.get(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch steps
  const { data: steps = [], isLoading: isLoadingSteps } = useQuery<SequenceStep[]>({
    queryKey: ['sequence-steps', id],
    queryFn: async () => {
      const response = await api.sequences.getSteps(id!);
      return response.data || [];
    },
    enabled: !!id,
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.sequences.validate(id!);
      return response.data;
    },
    onSuccess: (data) => {
      setValidationResult(data);
      if (data.valid) {
        toast.success('Sequence is valid');
      } else {
        toast.error('Sequence validation failed');
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to validate sequence: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.sequences.duplicate(id!, `${sequence?.name} (Copy)`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      toast.success('Sequence duplicated successfully');
      navigate(`/sequences/${data.id}/edit`);
    },
    onError: (error: any) => {
      toast.error(`Failed to duplicate sequence: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.sequences.delete(id!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      toast.success('Sequence deleted successfully');
      navigate('/sequences');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete sequence: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleEdit = () => {
    navigate(`/sequences/${id}/edit`);
  };

  const handleDuplicate = () => {
    duplicateMutation.mutate();
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const handleValidate = () => {
    validateMutation.mutate();
  };

  // Calculate total duration
  const calculateTotalDuration = (): string => {
    if (!steps || steps.length === 0) {
      return '0 days';
    }

    let totalHours = 0;
    let totalDays = 0;

    steps.forEach((step) => {
      if (step.delay_hours) {
        totalHours += step.delay_hours;
      }
      if (step.delay_days) {
        totalDays += step.delay_days;
      }
    });

    if (totalHours >= 24) {
      totalDays += Math.floor(totalHours / 24);
      totalHours = totalHours % 24;
    }

    const parts: string[] = [];
    if (totalDays > 0) {
      parts.push(`${totalDays} day${totalDays !== 1 ? 's' : ''}`);
    }
    if (totalHours > 0) {
      parts.push(`${totalHours} hour${totalHours !== 1 ? 's' : ''}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'Immediate';
  };

  // Loading state
  if (isLoading || isLoadingSteps) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <SequenceLoadingSkeleton type="detail" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !sequence) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Sequence not found</h3>
              <p className="mt-1 text-sm text-gray-500">The sequence you're looking for doesn't exist.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/sequences')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Back to Sequences
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/sequences')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Sequences
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{sequence.name}</h1>
                  <SequenceStatusBadge status={sequence.status} />
                </div>
                {sequence.description && (
                  <p className="text-sm text-gray-500">{sequence.description}</p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={handleValidate}
                  disabled={validateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Validate
                </button>
                <button
                  onClick={handleDuplicate}
                  disabled={duplicateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                  Duplicate
                </button>
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                {(sequence.status === 'draft' || sequence.status === 'archived') && (
                  <button
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="px-6 py-4 bg-gray-50 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center text-sm">
              <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Created</p>
                <p className="font-medium text-gray-900">
                  {new Date(sequence.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Updated</p>
                <p className="font-medium text-gray-900">
                  {new Date(sequence.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <div>
                <p className="text-gray-500">Steps</p>
                <p className="font-medium text-gray-900">
                  {steps.length} step{steps.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-medium text-gray-900">
                  {calculateTotalDuration()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Validation Results */}
            {validationResult && (
              <div className={`bg-white rounded-lg shadow p-6 border-l-4 ${
                validationResult.valid ? 'border-green-500' : 'border-red-500'
              }`}>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Validation Results</h2>
                {validationResult.valid ? (
                  <div className="flex items-center text-green-700">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">Sequence is valid</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center text-red-700 mb-2">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      <span className="font-medium">Validation failed</span>
                    </div>
                    {validationResult.errors && validationResult.errors.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-sm text-red-600">
                        {validationResult.errors.map((error: string, index: number) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {validationResult.warnings && validationResult.warnings.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-yellow-700 mb-2">Warnings:</h3>
                    <ul className="list-disc list-inside text-sm text-yellow-600">
                      {validationResult.warnings.map((warning: string, index: number) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Sequence Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Configuration</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Max Duration</label>
                  <p className="text-sm text-gray-900">
                    {sequence.max_duration_days ? `${sequence.max_duration_days} days` : 'No limit'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Allow Opt-Out</label>
                  <p className="text-sm text-gray-900">
                    {sequence.allow_opt_out === 'any_step' ? 'Any step' :
                     sequence.allow_opt_out === 'first_step' ? 'First step only' :
                     'Never'}
                  </p>
                </div>
              </div>
            </div>

            {/* Steps Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Sequence Steps</h2>
              {steps.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No steps in this sequence</p>
                </div>
              ) : (
                <SequenceTimeline steps={steps} />
              )}
            </div>

            {/* Steps List */}
            {steps.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Step Details</h2>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <SequenceStepCard
                      key={step.id}
                      step={step}
                      stepNumber={index + 1}
                      canEdit={false}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/campaigns/new?sequence=${id}`)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Use in Campaign
                </button>
                <button
                  onClick={handleEdit}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Edit Sequence
                </button>
                <button
                  onClick={handleDuplicate}
                  disabled={duplicateMutation.isPending}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Sequence'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">Delete Sequence</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete "{sequence.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

